/* eslint-disable class-methods-use-this */
import { moduleClientFactory } from '../../../../../api-service/module-client-factory';
import { ICompanyTagsClient } from '../../../../../api-service/clients';

export interface ICompanyTagsService {
  getTag(params: GetTagQuery): Promise<TagModel>;
  getTags(params: GetTagsQuery): Promise<TagModel[]>;
  getTagTypeOptions(): Promise<TagTypeOptionModel[]>;
  upsertTag(command: UpsertTagCommand): Promise<UpsertTagResult>;
  upsertTags(command: UpsertTagsCommand): Promise<UpsertTagsResult>;
  deleteTag(command: DeleteTagCommand): Promise<DeleteTagResult>;
  createMessageName(companyId: string): string;
}

export interface GetTagQuery {
  id: string;
}

export interface GetTagsQuery {
  companyId: string;
}

export interface UpsertTagCommand {
  tag: UpsertTagModel;
}

export interface UpsertTagResult {
  id: string;
}

export interface UpsertTagsCommand {
  companyIds: string[];
  tag: UpsertTagsModel;
}

export interface UpsertTagsResult {
  companyIds?: string[] | undefined;
}

export interface DeleteTagCommand {
  id: string;
}

export interface DeleteTagResult {
  id: string;
}

export interface TagModel {
  id: string;
  companyId: string;
  tagTypeId: string;
  startDate?: Date;
  endDate?: Date;
  name?: string;
}

export interface TagTypeOptionModel {
  id: string;
  name?: string | undefined;
  isActive: boolean;
  stateCode?: number | undefined;
  code?: string | undefined;
}

export interface UpsertTagModel {
  id: string;
  companyId: string;
  tagTypeId: string;
  startDate?: Date | string | undefined;
  endDate?: Date | string | undefined;
}

export interface UpsertTagsModel {
  tagTypeId: string;
  startDate?: Date | string | undefined;
  endDate?: Date | string | undefined;
}

export class CompanyTagsService implements ICompanyTagsService {
  private readonly companyTagsClient: ICompanyTagsClient;

  constructor() {
    this.companyTagsClient = moduleClientFactory.createCompanyTagsClient(true);
  }

  async getTag(params: GetTagQuery): Promise<TagModel> {
    const response = await this.companyTagsClient.getTag(params);

    if (!response || response?.tag === undefined) {
      return {} as TagModel;
    }

    const { tag } = response;

    return {
      id: tag.id,
      companyId: tag.companyId,
      tagTypeId: tag.tagTypeId,
      startDate: tag.startDate,
      endDate: tag.endDate,
      name: tag.name,
    } as TagModel;
  }

  async getTags(params: GetTagsQuery): Promise<TagModel[]> {
    const response = await this.companyTagsClient.getTags(params);

    if (!response || response?.tags === undefined) {
      return {} as TagModel[];
    }

    const s: TagModel[] = response.tags.map(
      p =>
        ({
          id: p.id,
          companyId: p.companyId,
          tagTypeId: p.tagTypeId,
          startDate: p.startDate,
          endDate: p.endDate,
          name: p.name,
        } as TagModel)
    );
    return s;
  }

  async getTagTypeOptions(): Promise<TagTypeOptionModel[]> {
    const response = await this.companyTagsClient.getTagTypeOptions();

    if (!response || response?.options === undefined) {
      return {} as TagTypeOptionModel[];
    }

    const s: TagTypeOptionModel[] = response.options.map(
      p =>
        ({
          id: p.id,
          name: p.name,
        } as TagTypeOptionModel)
    );
    return s;
  }

  async upsertTag(command: UpsertTagCommand): Promise<UpsertTagResult> {
    const response = await this.companyTagsClient.upsertTag({
      tag: command.tag,
    });

    if (!response || response?.id === undefined) {
      return {} as UpsertTagResult;
    }

    return {
      id: response.id,
    };
  }

  async upsertTags(command: UpsertTagsCommand): Promise<UpsertTagsResult> {
    const response = await this.companyTagsClient.upsertTags({
      companyIds: command.companyIds,
      tag: command.tag,
    });

    if (!response || response?.companyIds === undefined) {
      return {} as UpsertTagsResult;
    }

    return {
      companyIds: response.companyIds,
    };
  }

  async deleteTag(command: DeleteTagCommand): Promise<DeleteTagResult> {
    const response = await this.companyTagsClient.deleteTag({
      id: command.id,
    });

    if (!response || response?.id === undefined) {
      return {} as DeleteTagResult;
    }

    return {
      id: response.id,
    };
  }

  createMessageName(companyId: string) {
    return `subscribed to ${companyId} company-tags`;
  }
}
